import { useState } from "react";
import styles from "./RegisterForm.module.css";
import { registerFormSchema } from "../../../validation/formValidation";
import {
    Button,
    InlineMessage,
    TextField,
    Typography,
} from "@volvo/vcdk-react";
import { UITEXT } from "../../../constants/const";
import "@volvo/vcdk/themes/all-semantic.css";
import { sendVerificationEmail } from "../../../api/apiService";
import log from "../../../logging/logger";

const RegisterForm = () => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);

        if (!value) {
            setError("");
            return;
        }

        try {
            await registerFormSchema.validateAt("email", { email: value });
            setError("");
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError(String(err));
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const normalizedEmail = email.trim().toLowerCase();

        try {
            setIsLoading(true);
            await registerFormSchema.validate({ email: normalizedEmail });
            await sendVerificationEmail(normalizedEmail);
            setShowSuccess(true);
            setIsSubmitted(true);
            setError("");
        } catch (err: unknown) {
            log.error(err);
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError(String(err));
            }
            setShowSuccess(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <div className={styles.FormContainer}>
                <section className={styles.FormSection}>
                    <div className={styles.FormContent}>
                        <div>
                            <h1 className={styles.FormTitle}>
                                <Typography variant="heading2">
                                    {UITEXT.AppTitle}
                                </Typography>
                            </h1>
                        </div>
                        <form
                            className={styles.Form}
                            onSubmit={handleSubmit}
                            noValidate
                        >
                            <div className={styles.FormFieldGroup}>
                                <TextField
                                    className={styles.TextField}
                                    label="Email"
                                    type="email"
                                    floatingLabel
                                    value={email}
                                    onChange={handleChange}
                                    error={!!error}
                                    helperText={error}
                                    disabled={isLoading || isSubmitted}
                                />
                            </div>
                            <div className={styles.ButtonGroup}>
                                <Button
                                    variant="primary"
                                    size="large"
                                    fullWidth
                                    type="submit"
                                    disabled={
                                        !!error ||
                                        isSubmitted ||
                                        isLoading ||
                                        !email
                                    }
                                >
                                    {UITEXT.ConfirmEmail}
                                </Button>
                            </div>
                        </form>
                        <small className={styles.FooterText}>
                            <Typography variant="caption2">
                                {UITEXT.RegisterFooterText}
                            </Typography>
                        </small>
                        {showSuccess && (
                            <div
                                className={styles.InlineMessageContainer}
                                aria-live="polite"
                                role="status"
                            >
                                <InlineMessage
                                    variant="success"
                                    closable={false}
                                >
                                    <Typography variant="caption2">
                                        Please check your email in a moment to
                                        confirm your account.
                                    </Typography>
                                </InlineMessage>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default RegisterForm;
